/**
 * When the voice assistant should stay in conversation (overlay/rail open, no page chrome).
 * Aligned with server `voiceHelpers._shouldContinueConversation` and canonical gather phases.
 */

export const VOICE_CONTINUATION_PHASES = new Set([
  'gathering_data',
  'gathering_customer',
  'gathering_quote',
  'gathering_items',
  'confirmation',
  'contact_confirmation',
  'disambiguation',
  'quote_confirmation',
  'clarification',
]);

/**
 * Multi-step accounting intents — invoices, bills, expenses, bank, payments, contacts.
 * Used when the server returns `action: clarify` with a slot still open.
 */
export const VOICE_MULTI_STEP_INTENTS = new Set([
  'create_quote',
  'create_sales_invoice',
  'create_invoice',
  'create_purchase_invoice',
  'create_bill',
  'record_expense',
  'create_expense',
  'record_travel_expense',
  'record_office_expense',
  'record_bank_deposit',
  'record_bank_withdrawal',
  'record_bank_transfer',
  'record_transfer',
  'record_payment',
  'record_customer_payment',
  'record_supplier_payment',
  'record_payment_received',
  'record_payment_made',
  'create_contact',
  'create_customer',
  'create_supplier',
]);

export interface VoiceContinuationInput {
  success?: boolean;
  message?: string;
  action?: string;
  phase?: string;
  nextQuestion?: string;
  missingFields?: string[];
  needsConfirmation?: boolean;
  requires_confirmation?: boolean;
  data?: {
    phase?: string;
    intent?: string;
    missing_entity?: string;
    gathering_items?: boolean;
  };
  conversation_state?: {
    original_intent?: string;
    current_intent?: string;
    status?: string;
  };
}

function isNavigationOnlyResult(result: VoiceContinuationInput): boolean {
  return (
    result.action === 'navigate'
    || typeof (result.data as { route?: string } | undefined)?.route === 'string'
    || typeof (result.data as { destination?: string } | undefined)?.destination === 'string'
  );
}

function phaseIsContinuation(phase?: string | null): boolean {
  return Boolean(phase && VOICE_CONTINUATION_PHASES.has(phase));
}

function activeMultiStepIntent(result: VoiceContinuationInput): boolean {
  const intent =
    result.data?.intent
    || result.conversation_state?.original_intent
    || result.conversation_state?.current_intent;
  return typeof intent === 'string' && VOICE_MULTI_STEP_INTENTS.has(intent);
}

function messageLooksLikeFollowUp(message?: string): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return (
    message.includes('?')
    || /\b(what|which|who|how much|how many)\b/.test(lower)
    || /\b(please|need|required|provide|tell me|say the)\b/.test(lower)
  );
}

/**
 * True while the user is mid-flow (quote, invoice, expense, bank deposit, payment, contact, etc.).
 */
export function needsVoiceContinuation(result: VoiceContinuationInput): boolean {
  if (isNavigationOnlyResult(result)) return false;

  if (phaseIsContinuation(result.phase) || phaseIsContinuation(result.data?.phase)) {
    return true;
  }

  if (result.data?.gathering_items === true) return true;

  if (result.action === 'clarify' || result.action === 'confirm') return true;

  if (result.needsConfirmation === true || result.requires_confirmation === true) return true;

  if (result.data?.missing_entity) return true;

  if (result.nextQuestion || (result.missingFields && result.missingFields.length > 0)) {
    return true;
  }

  if (
    result.conversation_state?.status === 'awaiting_clarification'
    || result.conversation_state?.status === 'collecting_slots'
  ) {
    return true;
  }

  if (activeMultiStepIntent(result) && messageLooksLikeFollowUp(result.message)) {
    return true;
  }

  return messageLooksLikeFollowUp(result.message);
}
